import { Alert, Button, Group, Modal, Paper, Stack, Text, TextInput } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import React, { useState } from "react";
import {
  useGetMcbimFurnitureAndWallsOnFloorQuery,
  useGetMcbimItemsInSpaceQuery,
  useGetMcbimSpacesOnFloorQuery,
  usePostProjectByProjectIdIssuesCreateMutation,
} from "../../../../../../openapi";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import PixBimViewerV2 from "../../../../../components/PixBimViewerV2/PixBimViewerV2";
import { IssueDisabledColumnsEnum } from "../../../../../mc-shared/enums";
import {
  selectFloorplanRotation,
  selectConnectedBimSpacesForSelectedFloorSpaces,
  selectHttpMcbimSpacesOnSelectedFloor,
  selectMcbimItemsInSelectedSpaceArgs,
  selectMcbimSpacesOnSelectedFloorArgs,
  selectPixPathsForSelectedFloorCeilingGrids,
  selectRoomSearchText,
  selectSelectedFloor,
  selectSelectedFloorSpaceIds,
  selectSelectedFloorSpace,
  selectShowLengthItemsInSelectedRoom,
  selectViewerPixPolygonsForSelectedFloor,
} from "./floorplanSelectors";
import {
  clearSelectedFloorSpaceIdsInRedux,
  clearSelectedFloorSpacesInRedux,
  setFloorplanRotationInRedux,
  setRoomSearchTextInRedux,
  setSelectedFloorSpaceIdInRedux,
  setShowLengthItemsInSelectedRoomInRedux,
  toggleSelectedFloorSpaceIdInRedux,
} from "./floorplanSlice";
import FloorplanActionBar from "./FloorplanActionBar";
import FloorplanSelectedSpacePanel from "./FloorplanSelectedSpacePanel";
import { useIsShiftPressedRef } from "./useIsShiftPressedRef";
import { selectIssueBoard } from "../../issueSelectors";
import { useUpdateIssueBoardDisabledColumnsMutation } from "../../IssueBoardRTK";
import { thunkOpenIssueModalById } from "../../issueSlice";
import { selectIssueBoardId } from "../../redux/selectors/issueCommonSelectors";

const isPointInsidePolygon = (point: [number, number], polygon: number[][]) => {
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i]?.[0];
    const yi = polygon[i]?.[1];
    const xj = polygon[j]?.[0];
    const yj = polygon[j]?.[1];

    if (xi == null || yi == null || xj == null || yj == null) continue;

    const intersects = yi > point[1] !== yj > point[1] && point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi;

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

const FloorplanViewer: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newIssueTitle, setNewIssueTitle] = useState("");
  const dispatch = useAppDispatch();
  const shiftKeyPressedRef = useIsShiftPressedRef();
  const project = useAppSelector((state) => state.adminReducer.project);
  const issueBoard = useAppSelector(selectIssueBoard);
  const issueBoardId = useAppSelector(selectIssueBoardId);
  const selectedFloor = useAppSelector(selectSelectedFloor);
  const floorplanRotation = useAppSelector(selectFloorplanRotation);
  const roomSearchText = useAppSelector(selectRoomSearchText);
  const showLengthItemsInSelectedRoom = useAppSelector(selectShowLengthItemsInSelectedRoom);
  const mcbimSpacesOnSelectedFloorArgs = useAppSelector(selectMcbimSpacesOnSelectedFloorArgs);
  const mcbimItemsInSelectedSpaceArgs = useAppSelector(selectMcbimItemsInSelectedSpaceArgs);
  const selectedFloorSpaces = useAppSelector(selectHttpMcbimSpacesOnSelectedFloor);
  const selectedSpace = useAppSelector(selectSelectedFloorSpace);
  const selectedFloorSpaceIds = useAppSelector(selectSelectedFloorSpaceIds);
  const connectedBimSpacesForSelectedFloorSpaces = useAppSelector(selectConnectedBimSpacesForSelectedFloorSpaces);
  const viewerPolygons = useAppSelector(selectViewerPixPolygonsForSelectedFloor);
  const pixPaths = useAppSelector(selectPixPathsForSelectedFloorCeilingGrids);
  const romAttributeIsDisabled =
    issueBoard?.disabledColumns?.includes(IssueDisabledColumnsEnum.CONNECTEDBIMSPACES) === true;

  const [createIssue, { isLoading: isCreatingIssue }] = usePostProjectByProjectIdIssuesCreateMutation();
  const [updateIssueBoardDisabledColumns, { isLoading: isActivatingRomAttribute }] =
    useUpdateIssueBoardDisabledColumnsMutation();
  const { isFetching: selectedFloorSpacesFetching, isError: selectedFloorSpacesIsError } =
    useGetMcbimSpacesOnFloorQuery(mcbimSpacesOnSelectedFloorArgs ?? { projectNo: 0, floorName: "" }, {
      skip: mcbimSpacesOnSelectedFloorArgs == null,
    });
  useGetMcbimFurnitureAndWallsOnFloorQuery(mcbimSpacesOnSelectedFloorArgs ?? { projectNo: 0, floorName: "" }, {
    skip: mcbimSpacesOnSelectedFloorArgs == null,
  });
  useGetMcbimItemsInSpaceQuery(mcbimItemsInSelectedSpaceArgs ?? skipToken);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (issueBoardId == null) return;

      dispatch(clearSelectedFloorSpacesInRedux({ issueBoardId }));
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [dispatch, issueBoardId]);

  const canCreateIssueForSelectedRooms =
    project != null &&
    issueBoard != null &&
    connectedBimSpacesForSelectedFloorSpaces.length > 0 &&
    newIssueTitle.trim() !== "" &&
    !isCreatingIssue;

  const handleCreateIssueForSelectedRooms = async () => {
    if (!canCreateIssueForSelectedRooms || project == null || issueBoard == null) {
      return;
    }

    const createdIssueResp = await createIssue({
      projectId: project._id,
      body: {
        issueBoardId: issueBoard._id,
        title: newIssueTitle.trim(),
        connectedBimSpaces: connectedBimSpacesForSelectedFloorSpaces,
      },
    }).unwrap();

    setNewIssueTitle("");
    setIsCreateModalOpen(false);
    dispatch(clearSelectedFloorSpaceIdsInRedux({ issueBoardId: issueBoard._id }));
    dispatch(thunkOpenIssueModalById(createdIssueResp._id));
  };

  const handleActivateRomAttribute = async () => {
    if (project == null || issueBoard == null) {
      return;
    }

    await updateIssueBoardDisabledColumns({
      body: {
        disabledColumns: (issueBoard.disabledColumns ?? []).filter(
          (column) => column !== IssueDisabledColumnsEnum.CONNECTEDBIMSPACES,
        ),
      },
      params: {
        projectId: project._id,
        issueBoardId: issueBoard._id,
      },
    });
  };

  return (
    <>
      <Paper
        withBorder
        p={0}
        radius="md"
        style={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Stack
          gap={0}
          style={{
            flex: 1,
            minHeight: 0,
          }}
        >
          <FloorplanActionBar
            selectedFloor={selectedFloor}
            roomSearchText={roomSearchText}
            floorplanRotation={floorplanRotation}
            hasSelectedRoom={selectedSpace != null}
            selectedRoomCount={selectedFloorSpaceIds.length}
            showLengthItemsInSelectedRoom={showLengthItemsInSelectedRoom}
            manageIfcFilesUrl={project?._id != null ? `/project/${project._id}/settings/mcbim` : undefined}
            onRoomSearchTextChange={(value) => {
              if (issueBoardId == null) return;

              dispatch(setRoomSearchTextInRedux({ issueBoardId, roomSearchText: value }));
            }}
            onFloorplanRotationChange={(value) => {
              if (issueBoardId == null) return;

              dispatch(setFloorplanRotationInRedux({ issueBoardId, floorplanRotation: value }));
            }}
            onShowLengthItemsInSelectedRoomChange={(value) => {
              if (issueBoardId == null) return;

              dispatch(setShowLengthItemsInSelectedRoomInRedux({ issueBoardId, showLengthItemsInSelectedRoom: value }));
            }}
            onCreateIssueForSelectedRooms={() => setIsCreateModalOpen(true)}
            onClearSelectedRooms={() => {
              if (issueBoardId == null) return;

              dispatch(clearSelectedFloorSpaceIdsInRedux({ issueBoardId }));
            }}
          />

          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {romAttributeIsDisabled && (
              <Alert
                color="yellow"
                m="md"
                mb={0}
                title="Rom-attributtet er deaktivert"
                styles={{ message: { width: "100%" } }}
              >
                <Stack gap="xs" align="flex-start">
                  <Text size="sm">
                    Saker som opprettes fra plantegningen kan ikke vise romkobling før Rom er aktivert i registeret.
                  </Text>
                  <Button
                    size="xs"
                    variant="light"
                    color="blue"
                    loading={isActivatingRomAttribute}
                    onClick={() => void handleActivateRomAttribute()}
                  >
                    Aktiver rom
                  </Button>
                </Stack>
              </Alert>
            )}

            {selectedFloor === "" && (
              <Text size="sm" c="dimmed" p="md">
                Velg en etasje for å vise rom og plantegning.
              </Text>
            )}

            {selectedFloor !== "" && selectedFloorSpacesFetching && (
              <Text size="sm" c="dimmed" p="md">
                Laster rom for {selectedFloor}...
              </Text>
            )}

            {selectedFloor !== "" && selectedFloorSpacesIsError && (
              <Alert color="red" m="md">
                Kunne ikke hente rom for valgt etasje.
              </Alert>
            )}

            {selectedFloor !== "" && !selectedFloorSpacesFetching && !selectedFloorSpacesIsError && (
              <>
                {viewerPolygons.length > 0 ? (
                  <div style={{ flex: 1, minHeight: 0, height: "100%", display: "flex" }}>
                    <div
                      style={{
                        flex: 1,
                        minHeight: 0,
                        height: "100%",
                        padding: "0.75rem 1rem 1rem",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Text size="sm" c="dimmed">
                        {selectedFloorSpaces?.length ?? 0} rom lastet for {selectedFloor}.
                      </Text>

                      <div style={{ flex: 1, minHeight: 0 }}>
                        <PixBimViewerV2
                          polygons={viewerPolygons}
                          paths={pixPaths}
                          angle={floorplanRotation}
                          onClick={(id, coordinates) => {
                            if (id == null) {
                              if (!shiftKeyPressedRef.current) {
                                if (issueBoardId != null) {
                                  dispatch(setSelectedFloorSpaceIdInRedux({ issueBoardId, selectedFloorSpaceId: "" }));
                                }
                              }
                              return;
                            }

                            const clickedSpace = selectedFloorSpaces.find((space) => space._id === id);
                            if (clickedSpace) {
                              if (shiftKeyPressedRef.current) {
                                if (issueBoardId != null) {
                                  dispatch(
                                    toggleSelectedFloorSpaceIdInRedux({
                                      issueBoardId,
                                      selectedFloorSpaceId: clickedSpace._id,
                                    }),
                                  );
                                }
                                return;
                              }

                              if (issueBoardId != null) {
                                dispatch(
                                  setSelectedFloorSpaceIdInRedux({
                                    issueBoardId,
                                    selectedFloorSpaceId: clickedSpace._id,
                                  }),
                                );
                              }
                              return;
                            }

                            const spaceAtCoordinates = selectedFloorSpaces.find(
                              (space) =>
                                Array.isArray(space.polygon) &&
                                space.polygon.length > 2 &&
                                isPointInsidePolygon(coordinates, space.polygon),
                            );

                            if (shiftKeyPressedRef.current) {
                              if (spaceAtCoordinates != null) {
                                if (issueBoardId != null) {
                                  dispatch(
                                    toggleSelectedFloorSpaceIdInRedux({
                                      issueBoardId,
                                      selectedFloorSpaceId: spaceAtCoordinates._id,
                                    }),
                                  );
                                }
                              }
                              return;
                            }

                            if (issueBoardId != null) {
                              dispatch(
                                setSelectedFloorSpaceIdInRedux({
                                  issueBoardId,
                                  selectedFloorSpaceId: spaceAtCoordinates?._id ?? "",
                                }),
                              );
                            }
                          }}
                        />
                      </div>
                    </div>

                    {selectedSpace && <FloorplanSelectedSpacePanel />}
                  </div>
                ) : (
                  <Text size="sm" c="dimmed">
                    Ingen polygoner tilgjengelig for valgt etasje.
                  </Text>
                )}
              </>
            )}
          </div>
        </Stack>
      </Paper>

      <Modal
        opened={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title={`Ny sak for ${connectedBimSpacesForSelectedFloorSpaces.length} rom`}
        size="sm"
      >
        <Stack gap="sm">
          <TextInput
            data-autofocus
            placeholder="Skriv inn navn på sak"
            value={newIssueTitle}
            onChange={(event) => setNewIssueTitle(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && canCreateIssueForSelectedRooms) {
                event.preventDefault();
                void handleCreateIssueForSelectedRooms();
              }
            }}
          />

          <Text size="sm" c="dimmed">
            Saken kobles til {connectedBimSpacesForSelectedFloorSpaces.length} valgte rom.
          </Text>

          <Group justify="space-between">
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
              Avbryt
            </Button>
            <Button
              color="mc-primary"
              onClick={() => void handleCreateIssueForSelectedRooms()}
              disabled={!canCreateIssueForSelectedRooms}
              loading={isCreatingIssue}
            >
              Opprett
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default FloorplanViewer;
